import {
  AppState,
  ActualEntry,
  DayResult,
  RagLevel,
  RagThresholds,
  TransactionOccurrence,
  IncomeRule,
  ExpenseRule,
  BudgetItem,
  BalanceTarget,
} from '../types'
import { addDays, fromISODate, resolveDateRuleForMonth, toISODate } from './dateRules'
import {
  accruedBeforeAnchor,
  activeInterest,
  dailyInterest,
  isPaymentDate,
  rateLookup,
  roundToPenny,
} from './interest'

interface RecurrenceSource {
  frequency: IncomeRule['frequency']
  dateRule?: IncomeRule['dateRule']
  monthOfYear?: number
  dayOfWeek?: number
  startDate?: string
  endDate?: string
}

function withinBounds(date: Date, source: RecurrenceSource): boolean {
  if (source.startDate && date < fromISODate(source.startDate)) return false
  if (source.endDate && date > fromISODate(source.endDate)) return false
  return true
}

/** Expands a recurring rule into concrete occurrence dates within [rangeStart, rangeEnd]. */
export function expandRecurring(source: RecurrenceSource, rangeStart: Date, rangeEnd: Date): Date[] {
  const dates: Date[] = []

  if (source.frequency === 'monthly') {
    const dateRule = source.dateRule
    if (!dateRule) return dates
    let y = rangeStart.getFullYear()
    let m = rangeStart.getMonth()
    // step back one month in case a working-day-before rule for the *next*
    // month resolves into the tail end of the previous one — negligible cost.
    const endY = rangeEnd.getFullYear()
    const endM = rangeEnd.getMonth()
    while (y < endY || (y === endY && m <= endM)) {
      const d = resolveDateRuleForMonth(dateRule, y, m)
      if (d >= rangeStart && d <= rangeEnd && withinBounds(d, source)) {
        dates.push(d)
      }
      m++
      if (m > 11) {
        m = 0
        y++
      }
    }
    return dates
  }

  if (source.frequency === 'yearly') {
    const dateRule = source.dateRule
    if (!dateRule) return dates
    const month0 = (source.monthOfYear ?? 1) - 1
    // A working-day rule can move a date across a year boundary (1 Jan back
    // into December, 31 Dec forward into January), so check a year either side.
    for (let y = rangeStart.getFullYear() - 1; y <= rangeEnd.getFullYear() + 1; y++) {
      const d = resolveDateRuleForMonth(dateRule, y, month0)
      if (d >= rangeStart && d <= rangeEnd && withinBounds(d, source)) {
        dates.push(d)
      }
    }
    return dates
  }

  // weekly / four_weekly
  const step = source.frequency === 'weekly' ? 7 : 28
  const dow = source.dayOfWeek ?? 1
  let anchor = source.startDate ? fromISODate(source.startDate) : new Date(rangeStart)
  while (anchor.getDay() !== dow) anchor = addDays(anchor, 1)

  let cur = new Date(anchor)
  if (cur < rangeStart) {
    const diffDays = Math.floor((rangeStart.getTime() - cur.getTime()) / 86400000)
    const steps = Math.floor(diffDays / step)
    cur = addDays(cur, steps * step)
    while (cur < rangeStart) cur = addDays(cur, step)
  }
  while (cur <= rangeEnd) {
    if (withinBounds(cur, source)) dates.push(new Date(cur))
    cur = addDays(cur, step)
  }
  return dates
}

/**
 * What an outgoing costs on an occurrence date: its `amount`, or the latest
 * `amountChanges` entry dated on or before that day (SM-41). Always positive.
 */
export function expenseAmountOn(expense: Pick<ExpenseRule, 'amount' | 'amountChanges'>, isoDate: string): number {
  let amount = expense.amount
  let latest = ''
  for (const change of expense.amountChanges ?? []) {
    if (change.from <= isoDate && change.from >= latest) {
      amount = change.amount
      latest = change.from
    }
  }
  return Math.abs(amount)
}

export function ragForBalance(balance: number, thresholds: RagThresholds): RagLevel {
  if (balance <= thresholds.bad) return 'bad'
  if (balance <= thresholds.medium) return 'medium'
  return 'good'
}

/** Index of recorded actuals by `${sourceId}|${occurrence date}`. */
function actualLookup(state: AppState): Map<string, ActualEntry> {
  const map = new Map<string, ActualEntry>()
  for (const a of state.actuals) map.set(`${a.sourceId}|${a.date}`, a)
  return map
}

export interface OccurrenceVariance {
  date: string
  budgeted: number
  /** Undefined until an actual has been recorded for this occurrence. */
  actual?: number
  /** budgeted − actual: positive = came in under, negative = went over. */
  variance?: number
  /** Cumulative variance across every recorded occurrence up to and including this one. */
  runningVariance: number
  note?: string
}

/** The recurring items whose occurrences can carry a recorded actual. */
export type ActualsSource = BudgetItem | ExpenseRule | IncomeRule

/**
 * Every occurrence of a budget item, fixed outgoing or income in [rangeStart,
 * rangeEnd], paired with whatever actual has been recorded against it and a
 * running total of the variance. The running total is what turns "we went
 * over on the 20th" into "we're still £13 up over the month" — the
 * corrective picture.
 */
export function budgetVariance(
  state: AppState,
  budget: ActualsSource,
  rangeStart: Date,
  rangeEnd: Date,
): OccurrenceVariance[] {
  const actualByKey = actualLookup(state)
  let running = 0

  return expandRecurring(budget, rangeStart, rangeEnd).map((d) => {
    const date = toISODate(d)
    const budgeted = budget.kind === 'expense' ? expenseAmountOn(budget, date) : Math.abs(budget.amount)
    const actual = actualByKey.get(`${budget.id}|${date}`)
    if (!actual) return { date, budgeted, runningVariance: running }

    const spent = Math.abs(actual.amount)
    const variance = budgeted - spent
    running += variance
    return { date, budgeted, actual: spent, variance, runningVariance: running, note: actual.note }
  })
}

/**
 * Computes daily balances for [viewStart, viewEnd] inclusive.
 *
 * The starting balance is anchored to `startingBalanceDate`: it's the
 * opening balance on that date, before that date's own transactions. Days
 * on/after the anchor are always walked forward from it, so the anchor
 * date's balance always equals startingBalance (plus its own day's net
 * change), regardless of where the anchor sits relative to the requested
 * view.
 *
 * Days before the anchor have no recorded starting point, so projecting a
 * balance for them is necessarily speculative. That backward projection
 * only happens when `projectHistory` is on; otherwise those dates are
 * simply omitted from the result (the UI shows them as disabled/greyed).
 *
 * `includeAdjustments` (default true) applies active balance adjustments
 * (reconciliations) on top of the plan — the "actual" trajectory. Pass
 * false to get the "strict" plan as if none of them had happened.
 */
export function computeCashflow(
  state: AppState,
  viewStart: Date,
  viewEnd: Date,
  includeAdjustments = true,
): DayResult[] {
  const anchor = fromISODate(state.settings.startingBalanceDate)
  const projectHistory = state.settings.projectHistory
  const rangeStart = projectHistory ? (anchor < viewStart ? anchor : viewStart) : anchor
  const rangeEnd = anchor > viewEnd ? anchor : viewEnd

  if (rangeStart > rangeEnd) return []

  const byDate = new Map<string, TransactionOccurrence[]>()
  const pushTx = (date: Date, tx: TransactionOccurrence) => {
    if (date < rangeStart || date > rangeEnd) return
    const key = toISODate(date)
    const list = byDate.get(key) ?? []
    list.push(tx)
    byDate.set(key, list)
  }

  const actualByKey = actualLookup(state)

  for (const income of state.incomes) {
    if (!income.active) continue
    const expected = Math.abs(income.amount)
    const dates = expandRecurring(income, rangeStart, rangeEnd)
    for (const d of dates) {
      // Pay that really came to a different figure — a few pence off, or
      // Universal Credit paying more — replaces what was expected that day.
      const actual = actualByKey.get(`${income.id}|${toISODate(d)}`)
      pushTx(d, {
        sourceId: income.id,
        sourceKind: 'income',
        name: income.name,
        amount: actual ? Math.abs(actual.amount) : expected,
        category: income.category,
        notes: actual?.note,
        ...(actual ? { isActual: true, budgeted: expected } : {}),
      })
    }
  }

  for (const expense of state.expenses) {
    if (!expense.active) continue
    const dates = expandRecurring(expense, rangeStart, rangeEnd)
    for (const d of dates) {
      const planned = expenseAmountOn(expense, toISODate(d))
      // A variable bill — energy, water — recorded at what it really cost
      // replaces the planned figure, as a budget's actual does.
      const actual = actualByKey.get(`${expense.id}|${toISODate(d)}`)
      pushTx(d, {
        sourceId: expense.id,
        sourceKind: 'expense',
        name: expense.name,
        amount: -(actual ? Math.abs(actual.amount) : planned),
        category: expense.category,
        notes: actual?.note,
        ...(actual ? { isActual: true, budgeted: planned } : {}),
      })
    }
  }

  for (const budget of state.budgets) {
    if (!budget.active) continue
    const budgeted = Math.abs(budget.amount)
    const dates = expandRecurring(budget, rangeStart, rangeEnd)
    for (const d of dates) {
      // A recorded actual replaces the forecast for that occurrence, so the
      // projected balance reflects what really happened.
      const actual = actualByKey.get(`${budget.id}|${toISODate(d)}`)
      pushTx(d, {
        sourceId: budget.id,
        sourceKind: 'budget',
        name: budget.name,
        amount: -(actual ? Math.abs(actual.amount) : budgeted),
        category: budget.category,
        notes: actual?.note,
        isActual: !!actual,
        budgeted,
      })
    }
  }

  for (const oneOff of state.oneOffs) {
    const d = fromISODate(oneOff.date)
    pushTx(d, {
      sourceId: oneOff.id,
      sourceKind: 'oneoff',
      name: oneOff.name,
      amount: oneOff.amount,
      category: oneOff.category,
      notes: oneOff.notes,
    })
  }

  for (const transfer of state.transfers ?? []) {
    if (!transfer.active) continue
    const dates =
      transfer.frequency === 'once'
        ? transfer.date
          ? [fromISODate(transfer.date)]
          : []
        : expandRecurring({ ...transfer, frequency: transfer.frequency }, rangeStart, rangeEnd)
    const sign = transfer.direction === 'out' ? -1 : 1
    for (const d of dates) {
      pushTx(d, {
        sourceId: transfer.id,
        sourceKind: 'transfer',
        name: `${transfer.name} ${transfer.direction === 'out' ? 'to' : 'from'} ${transfer.counterpartyName}`,
        amount: sign * Math.abs(transfer.amount),
        notes: transfer.notes,
      })
    }
  }

  if (includeAdjustments) {
    for (const adjustment of state.adjustments) {
      if (!adjustment.active) continue
      const d = fromISODate(adjustment.date)
      pushTx(d, {
        sourceId: adjustment.id,
        sourceKind: 'adjustment',
        name: 'Balance adjustment',
        amount: adjustment.delta,
        notes: adjustment.note,
      })
    }
  }

  const netChangeOf = (key: string) => (byDate.get(key) ?? []).reduce((sum, t) => sum + t.amount, 0)

  const balanceByDate = new Map<string, number>()

  // Forward from the anchor date through rangeEnd. Interest is worked out on
  // the way, because each day's depends on that day's balance and the balance
  // includes the interest already paid. See lib/interest.ts.
  const interest = activeInterest(state.interest)
  const bandsOn = interest ? rateLookup(interest.rates) : undefined
  let accrued = interest ? accruedBeforeAnchor(interest, anchor, state.settings.startingBalance) : 0

  let balance = state.settings.startingBalance
  let cur = new Date(anchor)
  while (cur <= rangeEnd) {
    const key = toISODate(cur)

    // Paid at the start of the day, so it's in the balance that day's own
    // interest is worked out on.
    if (interest && isPaymentDate(cur, interest.paymentDay)) {
      const payment = roundToPenny(accrued)
      if (payment > 0) {
        const list = byDate.get(key) ?? []
        list.push({ sourceId: 'interest', sourceKind: 'interest', name: 'Interest', amount: payment })
        byDate.set(key, list)
      }
      accrued = 0
    }

    balance += netChangeOf(key)
    balanceByDate.set(key, balance)

    const bands = bandsOn?.(key)
    if (bands) accrued += dailyInterest(balance, bands)

    cur = addDays(cur, 1)
  }

  // Backward from the day before the anchor through rangeStart — only
  // when the user has opted into speculative history. Interest isn't
  // projected backwards: it depends on balances that were never recorded.
  if (projectHistory) {
    balance = state.settings.startingBalance
    cur = addDays(anchor, -1)
    while (cur >= rangeStart) {
      const key = toISODate(cur)
      balanceByDate.set(key, balance)
      balance -= netChangeOf(key)
      cur = addDays(cur, -1)
    }
  }

  const results: DayResult[] = []
  cur = new Date(rangeStart)
  while (cur <= rangeEnd) {
    const key = toISODate(cur)
    const transactions = byDate.get(key) ?? []
    const dayBalance = balanceByDate.get(key)!
    results.push({
      date: key,
      transactions,
      netChange: netChangeOf(key),
      balance: dayBalance,
      rag: ragForBalance(dayBalance, state.settings.ragThresholds),
      targetHits: [],
    })
    cur = addDays(cur, 1)
  }

  // Targets are evaluated over the whole computed run, not just the visible
  // window, so a streak that began before viewStart still counts.
  applyTargets(state, results)

  return results.filter((r) => {
    const d = fromISODate(r.date)
    return d >= viewStart && d <= viewEnd
  })
}

/**
 * Marks the days on which each active target is met. A day qualifies once the
 * balance has been at or above the target level for `consecutiveDays` running.
 *
 * Only the day that completes a run is marked, not every day after it — the
 * point is to show the moment the target was reached, not to shade in every
 * day you stayed above it. Dropping below and earning it again counts as a
 * fresh achievement and marks again. With `raiseBy` set, clearing a run also
 * steps the level up, so each rung of the ladder gets its own mark.
 */
function applyTargets(state: AppState, results: DayResult[]): void {
  for (const target of state.targets) {
    if (!target.active) continue
    const required = Math.max(1, Math.floor(target.consecutiveDays))
    const ratchet = target.raiseBy && target.raiseBy > 0 ? target.raiseBy : 0
    let level = target.amount
    let streak = 0
    let alreadyMarked = false

    for (const day of results) {
      if (day.balance >= level) {
        streak++
      } else {
        streak = 0
        alreadyMarked = false // the run is broken; earning it again counts
      }

      if (streak >= required && !alreadyMarked) {
        day.targetHits.push({
          targetId: target.id,
          name: target.name,
          level,
          style: target.style,
        })
        if (ratchet) {
          // The next rung has to be earned from scratch, so this run is over.
          level += ratchet
          streak = 0
        } else {
          alreadyMarked = true
        }
      }
    }
  }
}

export interface TargetAchievement {
  date: string // ISO yyyy-mm-dd the target was met on
  level: number // the level reached — higher than the base amount once ratcheted
}

/**
 * When each target is met, over [from, to], keyed by target id. Runs the same
 * projection the calendar does, so the dates line up with what's on it.
 */
export function targetAchievements(
  state: AppState,
  from: Date,
  to: Date,
): Map<string, TargetAchievement[]> {
  const byTarget = new Map<string, TargetAchievement[]>()
  for (const day of computeCashflow(state, from, to)) {
    for (const hit of day.targetHits) {
      const list = byTarget.get(hit.targetId) ?? []
      list.push({ date: day.date, level: hit.level })
      byTarget.set(hit.targetId, list)
    }
  }
  return byTarget
}

/**
 * The one achievement worth leading with: the most recent one already reached,
 * or failing that the next one coming up. Keeps ratcheting targets (which have
 * many) down to "where you are now" or "what's next".
 *
 * `reached` matters — most of the calendar is a forecast, so a date alone would
 * imply money you may not have yet.
 */
export function headlineAchievement(
  achievements: TargetAchievement[] | undefined,
  todayIso: string,
): { achievement: TargetAchievement; reached: boolean } | null {
  if (!achievements || achievements.length === 0) return null
  const past = achievements.filter((a) => a.date <= todayIso)
  if (past.length > 0) return { achievement: past[past.length - 1], reached: true }
  return { achievement: achievements[0], reached: false }
}

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function summarizeTarget(target: BalanceTarget, currencySymbol: string): string {
  const money = (n: number) =>
    `${currencySymbol}${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
  const held =
    target.consecutiveDays > 1 ? `held ${target.consecutiveDays} days` : 'on the day it lands'
  const ratchet = target.raiseBy ? `, then +${money(target.raiseBy)}` : ''
  return `${money(target.amount)} ${held}${ratchet}`
}

export function summarizeRule(rule: IncomeRule | ExpenseRule | BudgetItem): string {
  if ((rule.frequency === 'monthly' || rule.frequency === 'yearly') && 'dateRule' in rule && rule.dateRule) {
    const suffix =
      rule.dateRule.type === 'working_day_before'
        ? ' (working day before)'
        : rule.dateRule.type === 'working_day_after'
        ? ' (working day after)'
        : ''
    if (rule.frequency === 'yearly' && 'monthOfYear' in rule) {
      const month = MONTH_NAMES[(rule.monthOfYear ?? 1) - 1]
      const day = rule.dateRule.dayOfMonth === 'last' ? `last day of ${month}` : `${rule.dateRule.dayOfMonth} ${month}`
      return `Yearly, ${day}${suffix}`
    }
    const day = rule.dateRule.dayOfMonth === 'last' ? 'last day' : `day ${rule.dateRule.dayOfMonth}`
    return `Monthly, ${day}${suffix}`
  }
  const dowNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const dow = rule.dayOfWeek !== undefined ? dowNames[rule.dayOfWeek] : ''
  if (rule.frequency === 'weekly') return `Weekly, ${dow}`
  if (rule.frequency === 'four_weekly') return `Every 4 weeks, ${dow}`
  return rule.frequency
}
